/**
 * Centralized Mixpanel Tracking Service
 *
 * This service provides a generic way to track events across the application.
 * All global properties are automatically included with every event.
 */

// Mixpanel Project Token - replace with your actual token
const MIXPANEL_TOKEN = process.env.REACT_APP_MIXPANEL_TOKEN || "";

/**
 * Initialize Mixpanel SDK
 */
export const initMixpanel = () => {
  if (typeof window !== "undefined" && window.mixpanel && MIXPANEL_TOKEN) {
    window.mixpanel.init(MIXPANEL_TOKEN, {
      debug: process.env.NODE_ENV === "development",
      track_pageview: false, // We handle this manually
      persistence: "localStorage",
    });
  }
};

/**
 * Get browser name from user agent
 */
const getBrowser = () => {
  const ua = navigator.userAgent;
  if (ua.includes("Firefox")) return "Firefox";
  if (ua.includes("SamsungBrowser")) return "Samsung Browser";
  if (ua.includes("Opera") || ua.includes("OPR")) return "Opera";
  if (ua.includes("Trident")) return "Internet Explorer";
  if (ua.includes("Edge")) return "Edge";
  if (ua.includes("Edg")) return "Edge Chromium";
  if (ua.includes("Chrome")) return "Chrome";
  if (ua.includes("Safari")) return "Safari";
  return "Unknown";
};

/**
 * Get OS from user agent
 */
const getOS = () => {
  const ua = navigator.userAgent;
  if (ua.includes("Win")) return "Windows";
  if (ua.includes("Mac")) return "macOS";
  if (ua.includes("Linux")) return "Linux";
  if (ua.includes("Android")) return "Android";
  if (ua.includes("iPhone") || ua.includes("iPad")) return "iOS";
  return "Unknown";
};

/**
 * Get device ID (uses Mixpanel's distinct_id or generates one)
 */
const getDeviceId = () => {
  if (typeof window !== "undefined" && window.mixpanel) {
    return window.mixpanel.get_distinct_id?.() || "";
  }
  return "";
};

/**
 * Parse UTM parameters from URL
 */
const getUTMParams = () => {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source:
      params.get("utm_source") || localStorage.getItem("utm_source") || "",
    utm_medium:
      params.get("utm_medium") || localStorage.getItem("utm_medium") || "",
    utm_campaign:
      params.get("utm_campaign") || localStorage.getItem("utm_campaign") || "",
  };
};

/**
 * Store UTM params in localStorage for persistence across page loads
 */
export const persistUTMParams = () => {
  const params = new URLSearchParams(window.location.search);
  const utmSource = params.get("utm_source");
  const utmMedium = params.get("utm_medium");
  const utmCampaign = params.get("utm_campaign");

  if (utmSource) localStorage.setItem("utm_source", utmSource);
  if (utmMedium) localStorage.setItem("utm_medium", utmMedium);
  if (utmCampaign) localStorage.setItem("utm_campaign", utmCampaign);
};

/**
 * Get all global properties that should be sent with every event
 */
export const getGlobalProperties = () => {
  // Get session data from localStorage (set by NewFooter.jsx)
  const storedData = JSON.parse(localStorage.getItem("mixpannel_data") || "{}");
  const utmParams = getUTMParams();

  return {
    // Device & Technical
    "Device Id": getDeviceId(),
    Platform: "web",
    Browser: getBrowser(),
    OS: getOS(),

    // Session & Business (from stored data)
    "Room UUID":
      storedData["Room UUID"] ||
      localStorage.getItem("room_uuid_before_redirect") ||
      "",
    "Session Id": storedData["Session ID"] || "",
    "Business Id":
      storedData["Business ID"] || localStorage.getItem("business_id") || "",
    "Business Name": storedData["Business Name"] || "",
    "Room Number":
      storedData["Room Number"] || localStorage.getItem("room_number") || "",
    "Location Type":
      storedData["Location Type"] || localStorage.getItem("room_type") || "",

    // Location (can be enhanced with IP geolocation API)
    Country: localStorage.getItem("countryCode")?.toUpperCase() || "",
    City: localStorage.getItem("city")?.toUpperCase() || "",

    // UI
    Language: storedData["Language"] || sessionStorage.getItem("lang") || "EN",

    // UTM Parameters
    // "Utm Source": utmParams.utm_source || storedData["utm_source"] || "",
    // "Utm Medium": utmParams.utm_medium || storedData["utm_medium"] || "",
    // "Utm Campaign": utmParams.utm_campaign || storedData["utm_campaign"] || "",

    // Server
    "Server Region":
      storedData["Server Region"] ||
      process.env.REACT_APP_SERVER_REGION ||
      "STG",
  };
};

/**
 * Core tracking function - sends event with global properties
 */
export const trackEvent = (eventName, properties = {}) => {
  if (typeof window === "undefined" || !window.mixpanel) {
    console.warn("Mixpanel not initialized");
    return;
  }

  const globalProps = getGlobalProperties();
  const eventData = {
    ...globalProps,
    ...properties,
  };

  window.mixpanel.track(eventName, eventData);

  if (process.env.NODE_ENV === "development") {
    console.log(`[Mixpanel] ${eventName}`, eventData);
  }
};

/**
 * Identify user (call when user session is established)
 */
export const identifyUser = (userId, userProperties = {}) => {
  if (typeof window === "undefined" || !window.mixpanel) return;

  window.mixpanel.identify(userId);
  window.mixpanel.people.set(userProperties);
};

// ============================================================================
// EVENT TRACKING METHODS
// Each method corresponds to an event type from the tracking spec
// ============================================================================

// 1. SESSION & APP EVENTS
export const trackAppViewed = () => {
  trackEvent("App Viewed");
};

export const trackSessionStarted = () => {
  trackEvent("Session Started");
};

// 2. NAVIGATION & BROWSING EVENTS
export const trackCategoryViewed = ({
  category_id,
  category_name,
  category_type,
  is_external,
}) => {
  trackEvent("Category Viewed", {
    "Category Id": category_id,
    "Category Name": category_name,
    "Category Type": category_type,
    External: is_external || false,
  });
};

export const trackMenuItemViewed = ({
  item_id,
  item_name,
  category_name,
  price,
  currency,
  is_combo,
  is_external,
  quantity,
}) => {
  trackEvent("Item Viewed", {
    "Item Id": item_id,
    "Item Name": item_name,
    "Category Name": category_name,
    Price: price,
    Currency: currency,
    "Is Combo": is_combo || false,
    // Quantity: quantity || 1,
    External: is_external || false,
  });
};

export const trackBannerClicked = ({
  banner_id,
  banner_title,
  redirect_type,
  image_url,
}) => {
  trackEvent("Banner Clicked", {
    "Banner Id": banner_id,
    "Banner Title": banner_title,
    "Redirect Type": redirect_type,
    "Image URL": image_url,
  });
};

export const trackSearchUsed = ({ search_term, results_count }) => {
  trackEvent("Search Used", {
    "Search Term": search_term,
    "Results Count": results_count,
  });
};

export const trackLanguageChanged = ({ from_language, to_language }) => {
  trackEvent("Language Changed", {
    "From Language": from_language,
    "To Language": to_language,
  });
};

// 3. CART EVENTS
export const trackItemAddedToCart = ({
  item_id,
  item_name,
  category_name,
  quantity,
  unit_price,
  total_item_price,
  has_options,
  option_names,
}) => {
  trackEvent("Item Added to Cart", {
    "Item Id": item_id,
    "Item Name": item_name,
    "Category Name": category_name,
    Quantity: quantity,
    "Unit Price": unit_price,
    "Total Item Price": total_item_price,
    "Has Options": has_options || false,
    "Option Names": option_names || [],
  });
};

export const trackItemRemovedFromCart = ({ item_id, item_name }) => {
  trackEvent("Item Removed from Cart", {
    "Item Id": item_id,
    "Item Name": item_name,
  });
};

export const trackCartViewed = ({
  cart_items_count,
  cart_total_value,
  currency,
}) => {
  trackEvent("Cart Viewed", {
    "Cart Items Count": cart_items_count,
    "Cart Total Value": cart_total_value,
    Currency: currency,
  });
};

// 4. CHECKOUT & PAYMENT EVENTS
export const trackCheckoutStarted = ({
  cart_value,
  total_items,
  service_type,
}) => {
  trackEvent("Checkout Started", {
    "Cart Value": cart_value,
    "Total Items": total_items,
    "Service Type": service_type,
  });
};

export const trackPaymentSelected = ({ payment_method, gateway, amount }) => {
  trackEvent("Payment Selected", {
    "Payment Method": payment_method,
    Gateway: gateway,
    Amount: amount,
  });
};

export const trackOrderSubmitted = ({
  order_id,
  order_number,
  service_type,
  order_source,
  items_count,
  subtotal,
  tax,
  delivery_fee,
  other_fees,
  total_amount,
  is_external_service,
}) => {
  trackEvent("Order Submitted", {
    "Order Id": order_id,
    "Order Number": order_number,
    "Service Type": service_type,
    "Order Source": order_source,
    "Items Count": items_count,
    Subtotal: subtotal,
    Tax: tax,
    "Delivery Fee": delivery_fee || 0,
    "Other Fees": other_fees || 0,
    "Total Amount": total_amount,
    "Is External Service": is_external_service || false,
  });
};

export const trackOrderPaymentSucceeded = ({
  order_number,
  payment_method,
  gateway_transaction_id,
  amount,
  currency,
}) => {
  trackEvent("Order Payment Succeeded", {
    "Order Number": order_number,
    "Payment Method": payment_method,
    "Gateway Transaction Id": gateway_transaction_id,
    Amount: amount,
    Currency: currency,
  });
};

export const trackOrderPaymentFailed = ({
  order_number,
  amount,
  payment_method,
  gateway,
  failure_reason,
}) => {
  trackEvent("Order Payment Failed", {
    "Order Number": order_number,
    Amount: amount,
    "Payment Method": payment_method,
    Gateway: gateway,
    "Failure Reason": failure_reason,
  });
};

// 5. ORDER TRACKING EVENTS
export const trackOrderStatusViewed = () => {
  trackEvent("Order Status Viewed");
};

export const trackTaxiOrderTrackingOpened = ({ order_number }) => {
  trackEvent("Taxi Order Tracking Opened", {
    "Order Number": order_number,
  });
};

// 6. EXTERNAL SERVICE EVENTS
export const trackExternalServiceCategoryViewed = ({ service_type }) => {
  trackEvent("External Service Category Viewed", {
    "Service Type": service_type,
  });
};

export const trackExternalServiceOrderSubmitted = ({
  service_type,
  provider_name,
  amount,
  currency,
  order_reference,
}) => {
  trackEvent("External Service Order Submitted", {
    "Service Type": service_type,
    "Provider Name": provider_name,
    Amount: amount,
    Currency: currency,
    "Order Reference": order_reference,
  });
};

// 7. UX & ERROR TRACKING
export const trackAPIError = ({
  endpoint,
  method,
  status_code,
  error_message,
}) => {
  trackEvent("API Error", {
    Endpoint: endpoint,
    Method: method,
    "Status Code": status_code,
    "Error Message": error_message,
  });
};

export const trackPaymentGatewayError = ({
  gateway,
  error_code,
  error_message,
}) => {
  trackEvent("Payment Gateway Error", {
    Gateway: gateway,
    "Error Code": error_code,
    "Error Message": error_message,
  });
};

// Default export with all tracking methods
const mixpanel = {
  init: initMixpanel,
  track: trackEvent,
  identify: identifyUser,
  persistUTMParams,
  getGlobalProperties,

  // Session & App
  appViewed: trackAppViewed,
  sessionStarted: trackSessionStarted,

  // Navigation & Browsing
  categoryViewed: trackCategoryViewed,
  menuItemViewed: trackMenuItemViewed,
  bannerClicked: trackBannerClicked,
  searchUsed: trackSearchUsed,
  languageChanged: trackLanguageChanged,

  // Cart
  itemAddedToCart: trackItemAddedToCart,
  itemRemovedFromCart: trackItemRemovedFromCart,
  cartViewed: trackCartViewed,

  // Checkout & Payment
  checkoutStarted: trackCheckoutStarted,
  paymentSelected: trackPaymentSelected,
  orderSubmitted: trackOrderSubmitted,
  orderPaymentSucceeded: trackOrderPaymentSucceeded,
  orderPaymentFailed: trackOrderPaymentFailed,

  // Order Tracking
  orderStatusViewed: trackOrderStatusViewed,
  taxiOrderTrackingOpened: trackTaxiOrderTrackingOpened,

  // External Services
  externalServiceCategoryViewed: trackExternalServiceCategoryViewed,
  externalServiceOrderSubmitted: trackExternalServiceOrderSubmitted,

  // Errors
  apiError: trackAPIError,
  paymentGatewayError: trackPaymentGatewayError,
};

export default mixpanel;
